/**
 * AdvancedFilterPanel Component
 * Complete multi-dimensional filtering UI for advanced analytics
 * Integrates with FilterStore (Zustand) for state management
 */

import React, { useEffect, useState } from 'react';
import { BRAND_COLORS, TYPOGRAPHY } from '../types/data';
import { Filter, X, RefreshCw } from 'lucide-react';
import { MultiSelectDropdown, MultiSelectOption } from './MultiSelectDropdown';
import {
  useFilterStore,
  useHasActiveFilters,
  useActiveFilterCount,
} from '../stores/FilterStore';

interface AdvancedFilterPanelProps {
  disabled?: boolean;
  onFiltersApplied?: () => void;
}

export const AdvancedFilterPanel: React.FC<AdvancedFilterPanelProps> = ({
  disabled = false,
  onFiltersApplied,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  // Get filter state from Zustand store
  const {
    productCategories,
    categoryDomains,
    stages,
    stageBuckets,
    transactionRanges,
    countries,
    regions,
    continents,
    acquisitionTypes,
    companyTypes,
    availableFilters,
    isLoadingOptions,
    setProductCategories,
    setCategoryDomains,
    setStages,
    setStageBuckets,
    setTransactionRanges,
    setCountries,
    setRegions,
    setContinents,
    setAcquisitionTypes,
    setCompanyTypes,
    clearAllFilters,
    loadFilterOptions,
    syncFromURL,
  } = useFilterStore();

  const hasActiveFilters = useHasActiveFilters();
  const activeFilterCount = useActiveFilterCount();

  // Load filter options and sync from URL on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        syncFromURL(); // Load filters from URL first
        if (!availableFilters) {
          await loadFilterOptions(); // Fetch available options if not already loaded
        }
      } catch (error) {
        console.error('Failed to initialize filters:', error);
      }
    };

    initialize();
  }, []);

  // Notify parent when filters are applied
  useEffect(() => {
    if (hasActiveFilters && onFiltersApplied) {
      onFiltersApplied();
    }
  }, [
    productCategories,
    categoryDomains,
    stages,
    stageBuckets,
    transactionRanges,
    countries,
    regions,
    continents,
    acquisitionTypes,
    companyTypes,
  ]);

  const handleReloadOptions = async () => {
    setIsLoadingFilters(true);
    try {
      await loadFilterOptions();
    } catch (error) {
      console.error('Failed to reload filter options:', error);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  // Convert string arrays to MultiSelectOption format
  const toOptions = (values: string[]): MultiSelectOption[] =>
    values.map(v => ({ value: v, label: v }));

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Filter Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={disabled}
        aria-label={isExpanded ? 'Hide advanced filters' : 'Show advanced filters'}
        aria-expanded={isExpanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          fontSize: TYPOGRAPHY.sizes.base,
          fontWeight: TYPOGRAPHY.weights.medium,
          fontFamily: TYPOGRAPHY.fontFamily,
          border: `1px solid ${BRAND_COLORS.border}`,
          borderRadius: '8px',
          backgroundColor: hasActiveFilters ? BRAND_COLORS.primary : BRAND_COLORS.surface,
          color: hasActiveFilters ? '#FFFFFF' : BRAND_COLORS.text,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Filter size={18} />
        <span>Advanced Filters</span>
        {activeFilterCount > 0 && (
          <span
            style={{
              backgroundColor: hasActiveFilters ? '#FFFFFF' : BRAND_COLORS.primary,
              color: hasActiveFilters ? BRAND_COLORS.primary : '#FFFFFF',
              padding: '3px 10px',
              borderRadius: '12px',
              fontSize: TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.bold,
            }}
          >
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* Filter Panel Content */}
      {isExpanded && (
        <div
          style={{
            marginTop: '12px',
            padding: '20px',
            backgroundColor: BRAND_COLORS.surface,
            border: `1px solid ${BRAND_COLORS.border}`,
            borderRadius: '8px',
          }}
        >
          {/* Header with Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <h3
              style={{
                fontSize: TYPOGRAPHY.sizes.lg,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: BRAND_COLORS.text,
                margin: 0,
              }}
            >
              Filter Data
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Reload Options Button */}
              <button
                onClick={handleReloadOptions}
                disabled={disabled || isLoadingFilters}
                title="Reload filter options"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  fontSize: TYPOGRAPHY.sizes.sm,
                  fontWeight: TYPOGRAPHY.weights.medium,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  border: `1px solid ${BRAND_COLORS.border}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: BRAND_COLORS.textSecondary,
                  cursor: disabled || isLoadingFilters ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: disabled || isLoadingFilters ? 0.5 : 1,
                }}
              >
                <RefreshCw
                  size={14}
                  style={{
                    animation: isLoadingFilters ? 'spin 1s linear infinite' : 'none',
                  }}
                />
                Reload
              </button>

              {/* Clear All Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  disabled={disabled}
                  aria-label="Clear all filters"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.medium,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    border: `1px solid ${BRAND_COLORS.border}`,
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: BRAND_COLORS.textSecondary,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <X size={14} />
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Loading State */}
          {isLoadingOptions && !availableFilters && (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                color: BRAND_COLORS.textSecondary,
              }}
            >
              <RefreshCw
                size={24}
                style={{
                  animation: 'spin 1s linear infinite',
                  marginBottom: '12px',
                }}
              />
              <p style={{ margin: 0, fontSize: TYPOGRAPHY.sizes.sm }}>
                Loading filter options...
              </p>
            </div>
          )}

          {/* Filter Grid */}
          {availableFilters && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '16px',
              }}
            >
              {/* Product Categories */}
              {availableFilters.productCategories.length > 0 && (
                <MultiSelectDropdown
                  label="Product Categories"
                  options={toOptions(availableFilters.productCategories)}
                  selectedValues={productCategories}
                  onChange={setProductCategories}
                  disabled={disabled}
                />
              )}

              {/* Category Domains */}
              {availableFilters.categoryDomains.length > 0 && (
                <MultiSelectDropdown
                  label="Category Domains"
                  options={toOptions(availableFilters.categoryDomains)}
                  selectedValues={categoryDomains}
                  onChange={setCategoryDomains}
                  disabled={disabled}
                />
              )}

              {/* Stages */}
              {availableFilters.stages.length > 0 && (
                <MultiSelectDropdown
                  label="Funding Stages"
                  options={toOptions(availableFilters.stages)}
                  selectedValues={stages}
                  onChange={setStages}
                  disabled={disabled}
                />
              )}

              {/* Stage Buckets */}
              {availableFilters.stageBuckets.length > 0 && (
                <MultiSelectDropdown
                  label="Stage Buckets"
                  options={toOptions(availableFilters.stageBuckets)}
                  selectedValues={stageBuckets}
                  onChange={setStageBuckets}
                  disabled={disabled}
                />
              )}

              {/* Transaction Ranges */}
              {availableFilters.transactionRanges.length > 0 && (
                <MultiSelectDropdown
                  label="Transaction Ranges"
                  options={toOptions(availableFilters.transactionRanges)}
                  selectedValues={transactionRanges}
                  onChange={setTransactionRanges}
                  disabled={disabled}
                />
              )}

              {/* Countries */}
              {availableFilters.countries.length > 0 && (
                <MultiSelectDropdown
                  label="Countries"
                  options={toOptions(availableFilters.countries)}
                  selectedValues={countries}
                  onChange={setCountries}
                  disabled={disabled}
                />
              )}

              {/* Regions */}
              {availableFilters.regions.length > 0 && (
                <MultiSelectDropdown
                  label="Regions"
                  options={toOptions(availableFilters.regions)}
                  selectedValues={regions}
                  onChange={setRegions}
                  disabled={disabled}
                />
              )}

              {/* Continents */}
              {availableFilters.continents.length > 0 && (
                <MultiSelectDropdown
                  label="Continents"
                  options={toOptions(availableFilters.continents)}
                  selectedValues={continents}
                  onChange={setContinents}
                  disabled={disabled}
                />
              )}

              {/* Acquisition Types */}
              {availableFilters.acquisitionTypes.length > 0 && (
                <MultiSelectDropdown
                  label="Acquisition Types"
                  options={toOptions(availableFilters.acquisitionTypes)}
                  selectedValues={acquisitionTypes}
                  onChange={setAcquisitionTypes}
                  disabled={disabled}
                />
              )}

              {/* Company Types */}
              {availableFilters.companyTypes.length > 0 && (
                <MultiSelectDropdown
                  label="Company Types"
                  options={toOptions(availableFilters.companyTypes)}
                  selectedValues={companyTypes}
                  onChange={setCompanyTypes}
                  disabled={disabled}
                />
              )}
            </div>
          )}

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div
              style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: BRAND_COLORS.background,
                borderRadius: '6px',
                borderLeft: `4px solid ${BRAND_COLORS.primary}`,
              }}
            >
              <p
                style={{
                  fontSize: TYPOGRAPHY.sizes.sm,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  color: BRAND_COLORS.textSecondary,
                  margin: 0,
                }}
              >
                <strong style={{ color: BRAND_COLORS.text, fontWeight: TYPOGRAPHY.weights.semibold }}>
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                </strong>
                <span style={{ display: 'block', marginTop: '8px', fontSize: TYPOGRAPHY.sizes.xs }}>
                  {productCategories.length > 0 && `Product Categories: ${productCategories.length}`}
                  {categoryDomains.length > 0 && ` • Domains: ${categoryDomains.length}`}
                  {stages.length > 0 && ` • Stages: ${stages.length}`}
                  {stageBuckets.length > 0 && ` • Stage Buckets: ${stageBuckets.length}`}
                  {transactionRanges.length > 0 && ` • Ranges: ${transactionRanges.length}`}
                  {countries.length > 0 && ` • Countries: ${countries.length}`}
                  {regions.length > 0 && ` • Regions: ${regions.length}`}
                  {continents.length > 0 && ` • Continents: ${continents.length}`}
                  {acquisitionTypes.length > 0 && ` • Acquisition Types: ${acquisitionTypes.length}`}
                  {companyTypes.length > 0 && ` • Company Types: ${companyTypes.length}`}
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};
